import { AsyncPipe, DecimalPipe } from "@angular/common";
import { Component, computed, effect, inject, Signal, signal, viewChild } from "@angular/core";
import { toObservable, toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";
import { faArrowRightToBracket, faCheck, faUser } from "@fortawesome/free-solid-svg-icons";
import { ChartData } from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { BaseChartDirective, NgChartsModule } from "ng2-charts";
import { skip, switchMap, tap } from "rxjs";
import { District } from "src/app/core/models/district.model";
import { SchoolTeacherFilterOptions } from "src/app/core/models/filters";
import { School } from "src/app/core/models/school.model";
import { LoadingService } from "src/app/core/services/loading.service";
import { PartnerSuccessService, SchoolHealthScore } from "src/app/core/services/partner-success.service";
import { SharedModule } from "src/app/shared/shared.module";
import { PartnerSuccessHealthComponent } from "./partner-success-health.component";
import { SessionStorageService } from "src/app/core/services/session-storage.service";
import { FormsModule } from "@angular/forms";
import { UserRoles } from "src/app/core/models/user.model";
import { NgOptionComponent, NgSelectComponent } from "@ng-select/ng-select";


@Component({
  standalone: true,
  imports: [
    SharedModule,
    FontAwesomeModule,
    PartnerSuccessHealthComponent,
    DecimalPipe,
    NgChartsModule,
    AsyncPipe,
    FormsModule,
    NgSelectComponent,
    NgOptionComponent,
  ],
  styleUrl: 'partner-success.component.css',
  templateUrl: 'partner-success.component.html',
})
export class PartnerSuccessComponent {
  readonly partnerSuccessService = inject(PartnerSuccessService);
  readonly loadingService = inject(LoadingService);
  private readonly route = inject(ActivatedRoute);
  readonly userData = inject(SessionStorageService).userData;

  readonly partnerSuccessUsers$ = this.partnerSuccessService.getPartnerSuccessUsers();

  readonly districts = signal<District[]>(
    [{
      districtID: 0,
      name: "All",
      enabled: true,
    }].concat(
      this.route.snapshot.data['filterData'].districts.filter((district: District) =>
        district.name !== 'Clever District'
        && district.districtID > 1 // Don't show FIL District
      )
    )
  )
  readonly schools = signal<School[]>(
    this.route.snapshot.data['filterData'].schools.filter((school: School) =>
      school.name !== 'Clever School'
      && school.districtID > 1 // Don't show FIL District
    )
  )

  readonly UserRoles = UserRoles;
  readonly enrollmentIcon = faUser;
  readonly onboardedIcon = faCheck;
  readonly loginsIcon = faArrowRightToBracket;

  readonly schoolTeacherFilter = signal<SchoolTeacherFilterOptions>({
    district: null,
    school: null,
    teacher: null,
  });

  readonly health = toSignal(this.partnerSuccessService.getSchoolHealth(0, 0));

  readonly counts = computed(() => {
    const scores = this.scoresFiltered() ?? [];
    return {
      districts: (new Set(scores.map(s => s.district.districtID))).size,
      schools: scores.length,
      teachers: scores.reduce((a, s) => a + s.teacherCount, 0),
      students: scores.reduce((a, s) => a + s.studentCount, 0),
    };
  });

  readonly scores: Signal<SchoolHealthScore[]> = toSignal(
    toObservable(this.schoolTeacherFilter).pipe(
      skip(1),
      tap(() => this.loadingService.start()),
      switchMap(({ district, school }) => this.partnerSuccessService.getSchoolHealth(district?.districtID ?? 0, school?.schoolID ?? 0)),
      tap(() => this.loadingService.finish()),
    ),
    { initialValue: [] }
  );

  readonly partnerSuccessUserIDFilter = signal("0");
  readonly onboardedFilter = signal(false);

  readonly scoresFiltered = computed(() => (this.scores() ?? []).filter(s => {
    const userID = parseInt(this.partnerSuccessUserIDFilter());
    const onboardedOnly = this.onboardedFilter();
    return (!userID || s.school.partnerSuccessContact?.userID === userID) && (!onboardedOnly || s.onboarded);
  }));

  readonly healthCounts = computed(() => {
    const schoolOverallScores = (this.scoresFiltered() ?? [])
      .map(s => s.overallHealth);
    const healthy = schoolOverallScores.filter(s => 80 <= s).length;
    const risky = schoolOverallScores.filter(s => s < 60).length;
    return {
      healthy,
      some: schoolOverallScores.length - healthy - risky,
      risky,
    }
  });

  readonly percentOnboarded = computed(() => {
    const scores = this.scoresFiltered() ?? [];
    if (!scores || scores.length === 0) {
      return 0;
    }
    return Math.round(scores.filter(s => s.onboarded).length / scores.length * 100);
  });

  readonly percentSchoolsWithAtLeastOneLoginWithinLastWeek = computed(() => {
    const scores = this.scoresFiltered() ?? [];
    if (!scores || scores.length === 0) {
      return 0;
    }
    return Math.round(scores.filter(s => s.teachersWhoLoggedInOnceWithinLastWeekCount).length / scores.length * 100);
  });

  readonly chartPlugins = [ ChartDataLabels ];

  readonly gaugeChartOptions = {
    responsive: false,
    maintainAspectRatio: false,
    aspectRatio: 2,
    circumference: 180,
    rotation: -90,
    cutout: '75%',
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: { enabled: false },
    },
  }

  readonly gaugeChartData = computed<ChartData | undefined>(() => {
    const { healthy, some, risky } = this.healthCounts();
    const total = healthy + some + risky;
    if (!total) {
      return undefined;
    }
    const healthyPercentage = Math.round(healthy / total * 100);
    return {
      datasets: [{
        data: [healthyPercentage, 100 - healthyPercentage],
        backgroundColor: ['#0095DA', '#FFFFFF'],
        borderWidth: 0,
      }]
    };
  });

  readonly pieChartOptions = {
    responsive: false,
    plugins: {
      datalabels: {
        display: true,
        color: '#FFFFFF'
      },
    },
  }
  readonly pieChartData = computed<ChartData | undefined>(() => {
    const { healthy, some, risky } = this.healthCounts();
    const total = healthy + some + risky;
    if (!total) {
      return undefined;
    }
    return {
      labels: [ 'Healthy 80-100', 'Some Risk 60-79', 'At Risk 0-59' ],
      datasets: [{
        data: [healthy, some, risky],
        backgroundColor: [
          '#58B957',
          '#F2AE43',
          '#DB524B'
        ],
        hoverBackgroundColor: [
          '#58B957',
          '#F2AE43',
          '#DB524B'
        ],
      }]
    };
  });

  schoolTeacherFilterChange(options: SchoolTeacherFilterOptions) {
    this.schoolTeacherFilter.set(options);
    this.partnerSuccessUserIDFilter.set("0");
  }

  ngOnDestroy() {
    this.loadingService.finish();
  }
}
